/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import NavbarLangOnly from "../../components/Home/navbarLang/NavbarLangOnly";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "animate.css";
import { useAuth } from "../../context/auth/AuthContext";
import { saveTenantPlan } from "../utils/saveTenantPlan";
import {
  getSubscriptionPaymentContact,
  initiateSubscriptionPayment,
} from "../../Stores/utils/storeApi";

import PlansSection from "./PlansSection";
import ThemesSection from "./ThemesSection";

export default function ThemePlan() {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nextLoading, setNextLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);
  const [selectedThemeId, setSelectedThemeId] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [themeVariantMap, setThemeVariantMap] = useState({});
  const [existingTenantData, setExistingTenantData] = useState(null);
  const [tenantDataLoading, setTenantDataLoading] = useState(false);
  const handleThemeClick = (theme) => {
    const lastVariant = themeVariantMap[theme.id] || theme.variants[0];
    setSelectedThemeId(theme.id);
    setSelectedVariant(lastVariant);
  };

  const handleVariantClick = (themeId, variant) => {
    setSelectedThemeId(themeId);
    setSelectedVariant(variant);
    setThemeVariantMap(prev => ({
      ...prev,
      [themeId]: variant,
    }));
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const THEMES = [
    {
      id: 1,
      name: "Classic",
      variants: [
        {
          name: "Classic Brown",
          colors: {
            primary: "#C18064",
            primaryDark: "#A66B52",
            background: "#FAF5F3",
            secondaryBackground: "#FFF9E6",
          },
          img: "/themes/classic/brown.png",
        },
        {
          name: "Ocean Blue",
          colors: {
            primary: "#4A90E2",
            primaryDark: "#357ABD",
            background: "#F0F7FF",
            secondaryBackground: "#E6F2FF",
          },
          img: "/themes/classic/blue.png",
        },
        {
          name: "Royal Purple",
          colors: {
            primary: "#9B59B6",
            primaryDark: "#8E44AD",
            background: "#F8F4FB",
            secondaryBackground: "#F3E8FF",
          },
          img: "/themes/classic/purple.png",
        },
        {
          name: "Coral Red",
          colors: {
            primary: "#E74C3C",
            primaryDark: "#C0392B",
            background: "#FFF5F3",
            secondaryBackground: "#FFE6E3",
          },
          img: "/themes/classic/red.png",
        },
      ],
    },

    {
      id: 2,
      name: "Glow",
      variants: [
        {
          name: "Pink Glow",
          colors: {
            primary: "#EC4899",
            primaryDark: "#DB2777",
            primaryLight: "#F472B6",
            accent: "#FCD34D",
            accentDark: "#F59E0B",
          },
          img: "/themes/glow/pink.png",
        },
        {
          name: "Purple Dream",
          colors: {
            primary: "#A855F7",
            primaryDark: "#9333EA",
            primaryLight: "#C084FC",
            accent: "#F0ABFC",
            accentDark: "#D946EF",
          },
          img: "/themes/glow/purple.png",
        },
        {
          name: "Golden Luxe",
          colors: {
            primary: "#F59E0B",
            primaryDark: "#D97706",
            primaryLight: "#FBBF24",
            accent: "#FCD34D",
            accentDark: "#F59E0B",
          },
          img: "/themes/glow/golden.png",
        },
        {
          name: "Turquoise Glow",
          colors: {
            primary: "#14B8A6",
            primaryDark: "#0D9488",
            primaryLight: "#2DD4BF",
            accent: "#5EEAD4",
            accentDark: "#14B8A6",
          },
          img: "/themes/glow/turquoise.png",
        },
      ],
    },

    {
      id: 3,
      name: "Modern",
      variants: [
        { name: "original", colors: { primary: "#5B4DFF", primaryDark: "#483cdb", background: "#F9FAFB", text: "#5B4DFF", bg: "#5B4DFF", hover: "#483cdb" }, img: "/themes/modern/original.png" },
        { name: "royalBlue", colors: { primary: "#2563eb", primaryDark: "#1d4ed8", background: "#F9FAFB", text: "#2563eb", bg: "#2563eb", hover: "#1d4ed8" }, img: "/themes/modern/royal-blue.png" },
        { name: "forestGreen", colors: { primary: "#059669", primaryDark: "#047857", background: "#F9FAFB", text: "#059669", bg: "#059669", hover: "#047857" }, img: "/themes/modern/forest-green.png" },
        { name: "burntOrange", colors: { primary: "#ea580c", primaryDark: "#c2410c", background: "#F9FAFB", text: "#ea580c", bg: "#ea580c", hover: "#c2410c" }, img: "/themes/modern/burnt-orange.png" },
      ],
    },

  ];


  useEffect(() => {
    const fetchExistingTenantData = async () => {
      const tenantId = localStorage.getItem("tenantId");
      if (!tenantId) return;

      try {
        setTenantDataLoading(true);
        const baseUrl =
          process.env.NODE_ENV === "development"
            ? "/api"
            : (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, "");

        const res = await fetch(`${baseUrl}/GetAllTenantFullDetails?tenantId=${tenantId}`, {
          headers: {
            Accept: "application/json",
            Authorization: localStorage.getItem("authToken")
              ? `Bearer ${localStorage.getItem("authToken")}`
              : ""
          }
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const tenantInfo = Array.isArray(data) ? data[0] : null;
        if (!tenantInfo) return;

        setExistingTenantData(tenantInfo);

        localStorage.setItem("companyStoreName", tenantInfo.BusinessName || tenantInfo.CompanyName || tenantInfo.TenantName || "");
        localStorage.setItem("companySector", tenantInfo.SectorType || "");
        localStorage.setItem("companyStoreSlug", tenantInfo.storeSlug || tenantInfo.StoreSlug || "");
        localStorage.setItem("tenantEmail", tenantInfo.Email || "");
        localStorage.setItem("tenantPhone", tenantInfo.Phone || "");
        localStorage.setItem("TenantDtlId", String(tenantInfo.TenantDtlId || 0));
        localStorage.setItem("tenantDetails", JSON.stringify(tenantInfo));

        const themeId = Number(tenantInfo.ThemeId || 1);
        const themeObj = THEMES.find((t) => t.id === themeId) || THEMES[0];
        const variant =
          themeObj?.variants.find((v) => v.name === tenantInfo.ColorName) ||
          themeObj?.variants?.[0] ||
          null;

        if (themeObj && variant) {
          setSelectedThemeId(themeObj.id);
          setSelectedVariant(variant);
          setThemeVariantMap((prev) => ({ ...prev, [themeObj.id]: variant }));
          localStorage.setItem("selectedThemeId", String(themeObj.id));
          localStorage.setItem("selectedThemeVariant", variant.name);
        }
      } catch (err) {
        console.error("Failed to fetch existing tenant data:", err);
      } finally {
        setTenantDataLoading(false);
      }
    };

    fetchExistingTenantData();
  }, []);

  useEffect(() => {
    const savedThemeId = parseInt(localStorage.getItem("selectedThemeId") || "0");
    const savedVariantName = localStorage.getItem("selectedThemeVariant");


    if (!savedThemeId || !savedVariantName) return;

    const themeObj = THEMES.find(t => t.id === savedThemeId);
    if (!themeObj) return;

    const variant = themeObj.variants.find(v => v.name === savedVariantName);
    if (!variant) return;

    setSelectedThemeId(themeObj.id);
    setSelectedVariant(variant);
    setThemeVariantMap({ [themeObj.id]: variant });
  }, []);


  const COLORS = {
    darkPurple: "#21003D",
    purple: "#7400BF",
    gold: "#F2B441",
    gradient: "linear-gradient(135deg, #21003D, #7400BF)",
    textLight: "#f3e9ff",
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const getApiUrl = () => {
          if (process.env.NODE_ENV === "development") {
            return "/api";
          }
          return (
            process.env.REACT_APP_API_BASE_URL ||
            "https://madar-api.madar.iq/api"
          );
        };


        const baseUrl = getApiUrl().replace(/\/+$/, "");

        const res = await fetch(`${baseUrl}/Plans/GetPlan?PlanId=-1`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
        if (!res.ok) throw new Error("HTTP " + res.status);

        const text = await res.text();
        if (!text.startsWith("[") && !text.trim().startsWith("{"))
          throw new Error("Invalid response");

        const data = JSON.parse(text);

        const safeString = (val) => {
          if (val === null || val === undefined) return "";
          if (typeof val === "string") {
            const trimmed = val.trim();
            if (
              trimmed === "{}" ||
              trimmed === "null" ||
              trimmed === "undefined" ||
              trimmed === "[object Object]"
            ) {
              return "";
            }
            return trimmed;
          }
          if (typeof val === "number") return String(val);
          if (typeof val === "boolean") return String(val);
          if (typeof val === "object") {
            if (Array.isArray(val)) return "";
            if (Object.keys(val).length === 0) return "";
            return "";
          }
          try {
            const str = String(val);
            if (str === "[object Object]" || str === "{}") return "";
            return str.trim();
          } catch {
            return "";
          }
        };

        const formatted = data.map((p) => {
          const notes = [];

          for (let i = 1; i <= 8; i++) {
            let n = null;

            if (isArabic) {
              n = p[`note${i}`];
              if (!n || safeString(n) === "") {
                n = p[`engNote${i}`];
              }
            } else {
              n = p[`engNote${i}`];
              if (!n || safeString(n) === "") {
                n = p[`note${i}`];
              }
            }

            const noteStr = safeString(n);
            if (
              noteStr &&
              noteStr !== "" &&
              noteStr !== "{}" &&
              noteStr !== "null" &&
              noteStr !== "undefined" &&
              noteStr !== "[object Object]" &&
              noteStr.length > 0
            ) {
              const finalNote =
                typeof noteStr === "string" ? noteStr : String(noteStr);
              if (finalNote && finalNote !== "[object Object]") {
                notes.push(finalNote);
              }
            }
          }

          let title, desc;
          if (isArabic) {
            title = safeString(p.planType) || safeString(p.engPlanType);
            desc = safeString(p.descripsion) || safeString(p.engDescripsion);
          } else {
            title = safeString(p.engPlanType) || safeString(p.planType);
            desc = safeString(p.engDescripsion) || safeString(p.descripsion);
          }

          let priceValue = p.price;
          if (typeof priceValue === "object") {
            priceValue = 0;
          } else if (typeof priceValue === "string") {
            const parsed = parseFloat(priceValue);
            priceValue = isNaN(parsed) ? 0 : parsed;
          } else if (typeof priceValue !== "number") {
            priceValue = 0;
          }

          return {
            id: p.id || p.planId || Math.random(),
            planId: p.planId,
            isActive: p.isActive,
            title: title || (isArabic ? "باقة" : "Plan"),
            engTitle: safeString(p.engPlanType) || "",
            desc: desc || "",
            price: priceValue,
            notes,
          };
        });

        const activePlans = formatted.filter((p) => p.isActive === 1);
        setPlans(activePlans);
        console.log("✅ Plans loaded:", formatted);
      } catch (err) {
        console.error("❌ Fetch error:", err);
        toast.error(
          isArabic ? "فشل تحميل الباقات 🚫" : "Failed to load plans 🚫"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [isArabic, i18n.language]);

  const applyTheme = (themeName, variant) => {
    if (!variant || !variant.colors) return;

    const root = document.documentElement;
    Object.entries(variant.colors).forEach(([key, value]) => {
      root.style.setProperty(`--ws-${key}`, value);
    });
  };


  const handleNext = async () => {
    const companyStoreName =
      localStorage.getItem("companyStoreName") ||
      existingTenantData?.BusinessName ||
      existingTenantData?.CompanyName ||
      existingTenantData?.TenantName ||
      "";
    const companySector =
      localStorage.getItem("companySector") ||
      existingTenantData?.SectorType ||
      "";

    if (!companyStoreName || !companySector) {
      toast.error(
        isArabic
          ? "⚠️ يرجى إكمال بيانات المتجر أولاً"
          : "⚠️ Please complete company information first",
        { rtl: isArabic, theme: "colored" }
      );
      setTimeout(() => navigate("/company-info"), 2000);
      return;
    }

    if (!selectedPlan) {
      toast.error(isArabic ? "الرجاء اختيار الخطة ⚠️" : "Please select a plan ⚠️");
      return;
    }

    const themeObj = THEMES.find((t) => t.id === selectedThemeId);
    if (!themeObj) {
      toast.error(isArabic ? "الرجاء اختيار ثيم صحيح ⚠️" : "Please select a valid theme ⚠️");
      return;
    }

    const variantObj = selectedVariant || themeObj.variants?.[0];
    if (!variantObj) {
      toast.error(isArabic ? "الرجاء اختيار لون الثيم ⚠️" : "Please select theme color ⚠️");
      return;
    }

    setNextLoading(true);

    const plan = plans.find((p) => Number(p.planId) === Number(selectedPlan));
    if (!plan || !plan.planId || plan.planId === 0) {
      toast.error(isArabic ? "الخطة غير موجودة ❌" : "Plan not found ❌");
      setNextLoading(false);
      return;
    }

    const isFreePlan = !plan.price || plan.price === 0 || plan.price === "0" || plan.price === "free" || plan.price === null;

    try {
      const nowIso = new Date().toISOString();
      const durationDays =
        isFreePlan ? 7 :
        String(plan.title || plan.engTitle || "").toLowerCase().includes("week") ? 7 :
        String(plan.title || plan.engTitle || "").toLowerCase().includes("month") ? 30 :
        30;

      localStorage.setItem("subscriptionStartDate", nowIso);
      localStorage.setItem("subscriptionDurationDays", String(durationDays));

      const now = new Date();
      localStorage.setItem("plan_renewed_at", now.toISOString());

      const expiryDate = new Date(now.getTime() + (isFreePlan ? 7 : 30) * 24 * 60 * 60 * 1000);
      localStorage.setItem("plan_expiry_date", expiryDate.toISOString());

      localStorage.removeItem("force_expiry_notice");
      sessionStorage.removeItem("trialExpiredAlertShown");
      sessionStorage.removeItem("paidExpiredAlertShown");

      if (isFreePlan) {
        localStorage.setItem("trialUsed", "1");
        if (!localStorage.getItem("trialStartDate")) {
          localStorage.setItem("trialStartDate", nowIso);
        }
      }

      localStorage.setItem("selectedPlan", plan.title);
      localStorage.setItem("selectedPlanId", plan.planId);
      localStorage.setItem("selectedPlanPrice", plan.price);
      localStorage.setItem("selectedThemeId", themeObj.id.toString());
      localStorage.setItem("selectedThemeVariant", variantObj.name);
      localStorage.setItem("selectedPlanType", plan.title || "");
      localStorage.setItem("selectedEngPlanType", plan.engTitle || "");
      localStorage.setItem("selectedPlanDesc", plan.desc || "");

      if (plan.notes && Array.isArray(plan.notes)) {
        for (let i = 1; i <= 8; i++) {
          localStorage.setItem(`selectedPlanNote${i}`, plan.notes[i - 1] || "");
        }
      } else {
        for (let i = 1; i <= 8; i++) {
          localStorage.setItem(`selectedPlanNote${i}`, "");
        }
      }

      const tenantId = localStorage.getItem("tenantId");
      const tenantEmail = localStorage.getItem("tenantEmail") || existingTenantData?.Email || "";
      const tenantPhone = localStorage.getItem("tenantPhone") || existingTenantData?.Phone || "";
      const companySectorLocal = localStorage.getItem("companySector") || "";
      const tenantDtlId = localStorage.getItem("TenantDtlId") || existingTenantData?.TenantDtlId || 0;
      const companyPaymentMethods = JSON.parse(localStorage.getItem("companyPaymentMethods") || "[]");

      const formData = new FormData();
      const tenantDetails = JSON.parse(localStorage.getItem("tenantDetails") || "{}");
      const finalCreatedAt = tenantDetails.createdAt || new Date().toISOString();
      const companyStoreSlug = localStorage.getItem("companyStoreSlug") || tenantDetails.storeSlug || "";

      formData.append("TenantId", tenantId || 0);
      formData.append("TenantName", companyStoreName);
      formData.append("EngTenantName", companyStoreName);
      formData.append("Email", tenantEmail);
      formData.append("Phone", tenantPhone);
      formData.append("SubDomain", "");
      formData.append("IsActive", 1);
      formData.append("CreatedAt", finalCreatedAt);
      formData.append("Product", "");
      formData.append("StoreSlug", companyStoreSlug);
      formData.append("MainPlanId", plan.planId);
      formData.append("TenantDtlId", tenantDtlId);
      formData.append("BusinessName", companyStoreName);
      formData.append("CompanyName", companyStoreName);
      formData.append("ThemeId", themeObj.id);
      formData.append("DetailPlanId", plan.planId);
      formData.append("ColorName", variantObj.name);
      formData.append("PlanType", plan.title);
      formData.append("EngPlanType", plan.engTitle || "");
      formData.append("Price", plan.price);
      formData.append("Descripsion", plan.desc || "");
      formData.append("EngDescripsion", "");

      for (let i = 1; i <= 8; i++) {
        formData.append(`Note${i}`, localStorage.getItem(`selectedPlanNote${i}`) || "");
      }
      formData.append("PaymentMethods", JSON.stringify(companyPaymentMethods));

      const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, "");
      const token = localStorage.getItem("authToken");

      let finalStoreSlug = companyStoreSlug;
      if (!finalStoreSlug && companyStoreName) {
        finalStoreSlug = companyStoreName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        localStorage.setItem("companyStoreSlug", finalStoreSlug);
      }
      formData.set("StoreSlug", finalStoreSlug);
      formData.append("planId", plan.planId);

      const res = await fetch(`${baseUrl}/UpdateTenantDetails/UpdateTenantFullDetails`, {
        method: "POST",
        headers: { Authorization: token ? `Bearer ${token}` : "" },
        body: formData,
      });

      if (!res.ok) throw new Error("UpdateTenantDetails failed");

      const updateData = await res.json();
      const updatedDetails = { ...tenantDetails, ...updateData };
      localStorage.setItem("tenantDetails", JSON.stringify(updatedDetails));

      try {
        const gRes = await fetch(`${baseUrl}/PaymentMethods/GetPaymentMethods?TenantId=-1`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" }
        });
        if (gRes.ok) {
          const allGlobal = await gRes.json();
          const payload = companyPaymentMethods.map(selectedName => {
            const found = Array.isArray(allGlobal) && allGlobal.find(g =>
              (g.paymentMethodName || "").toLowerCase().includes(selectedName.toLowerCase())
            );
            return {
              tenantId: parseInt(tenantId),
              paymentMethodId: found ? (found.paymentMethodId || found.id) : 0,
              paymentMethodName: selectedName,
              engPaymentMethodName: selectedName,
              isActive: 1
            };
          });
          if (payload.length > 0) {
            await fetch(`${baseUrl}/PaymentMethods/SavePaymentMethods`, {
              method: "POST",
              headers: { "Content-Type": "application/json", "Authorization": token ? `Bearer ${token}` : "" },
              body: JSON.stringify(payload)
            });
          }
        }
      } catch (pmErr) {
        console.error("Error saving payment methods:", pmErr);
      }

      if (isFreePlan) {
        await saveTenantPlan(plan, isArabic);
        toast.success(isArabic ? "تم حفظ المتجر والخطة بنجاح ✅" : "Store and plan saved successfully ✅");
        navigate("/admin-store");
      } else {
        const contact = getSubscriptionPaymentContact();
        const paymentRes = await initiateSubscriptionPayment({
          tenantId: parseInt(tenantId, 10),
          planId: plan.planId,
          email: contact.email || tenantEmail,
          phone: contact.phone || tenantPhone,
          firstName: contact.firstName,
          lastName: contact.lastName,
        });

        if (!paymentRes?.success || !paymentRes?.paymentUrl) {
          toast.error(
            paymentRes?.error ||
              (isArabic ? "فشل فتح صفحة الدفع ❌" : "Failed to open payment page ❌")
          );
          return;
        }

        toast.success(
          isArabic ? "جارٍ تحويلك لصفحة الدفع..." : "Redirecting to payment..."
        );
        window.location.href = String(paymentRes.paymentUrl).trim();
      }
    } catch (err) {
      console.error("Error saving:", err);
      toast.error(isArabic ? "حدث خطأ أثناء الحفظ ❌" : "An error occurred while saving ❌");
    } finally {
      setNextLoading(false);
    }
  };


  return (
    <>
      <NavbarLangOnly />
      <ToastContainer />

      <div
        style={{
          background: COLORS.gradient,
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "30px",
          fontFamily: "'Tajawal', 'Cairo', sans-serif",
          direction: isArabic ? "rtl" : "ltr",
        }}
      >
        <div
          style={{
            maxWidth: isMobile ? "100%" : 900,
            width: "100%",
            background: "rgba(255,255,255,0.08)",
            borderRadius: isMobile ? 14 : 18,
            padding: isMobile ? "25px 15px" : "40px 30px",
            boxShadow: "0 8px 25px rgba(0,0,0,0.3)",
            border: "1px solid rgba(255,255,255,0.15)",
            backdropFilter: "blur(12px)",
            color: "#fff",
            margin: isMobile ? "10px" : "0",
          }}
          className="animate__animated animate__fadeInUp"
        >
          <div className="text-center mb-4">
            <h3
              className="fw-bold mb-2"
              style={{
                color: COLORS.gold,
                fontFamily: "'Cairo', sans-serif",
                fontSize: isMobile ? "1.4rem" : "1.8rem",
                padding: isMobile ? "0 10px" : "0",
              }}
            >
              {isArabic
                ? "اختر خطتك وثيم متجرك"
                : "Choose Your Plan & Store Theme"}
            </h3>
          </div>

          <PlansSection
            loading={loading}
            plans={plans}
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            isArabic={isArabic}
            COLORS={COLORS}
            isMobile={isMobile}
          />
          {existingTenantData ? (
            <div
              style={{
                marginTop: 18,
                padding: isMobile ? "14px" : "18px",
                borderRadius: 14,
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.12)"
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "flex-start" : "center",
                  justifyContent: "space-between",
                  gap: 10
                }}
              >
                <div>
                  <h4 style={{ margin: 0, color: COLORS.gold, fontWeight: 700 }}>
                    {isArabic ? "بيانات المتجر الحالية محفوظة" : "Current store data is preserved"}
                  </h4>
                  <p style={{ margin: "6px 0 0", opacity: 0.85, fontSize: 14 }}>
                    {isArabic
                      ? "سيتم الاحتفاظ بالثيم وباقي معلومات المتجر القديمة، والمطلوب فقط اختيار الخطة الجديدة."
                      : "Your existing theme and store details will stay unchanged. Only the new plan selection is required."}
                  </p>
                </div>
                <div
                  style={{
                    padding: "8px 12px",
                    borderRadius: 999,
                    background: "rgba(242,180,65,0.12)",
                    border: "1px solid rgba(242,180,65,0.35)",
                    color: COLORS.gold,
                    fontWeight: 700,
                    fontSize: 13
                  }}
                >
                  {tenantDataLoading
                    ? isArabic ? "جاري تحميل البيانات..." : "Loading data..."
                    : `${existingTenantData.ColorName || selectedVariant?.name || ""} • ${existingTenantData.PlanType || existingTenantData.EngPlanType || ""}`}
                </div>
              </div>
            </div>
          ) : (
            <ThemesSection
              themes={THEMES}
              selectedThemeId={selectedThemeId}
              selectedVariant={selectedVariant}
              handleThemeClick={(theme) => {
                const v = themeVariantMap[theme.id] || theme.variants[0];
                setSelectedThemeId(theme.id);
                setSelectedVariant(v);
                applyTheme(theme.name, v);
              }}
              handleVariantClick={(themeId, variant) => {
                setSelectedThemeId(themeId);
                setSelectedVariant(variant);
                setThemeVariantMap(prev => ({ ...prev, [themeId]: variant }));
                const theme = THEMES.find(t => t.id === themeId);
                applyTheme(theme.name, variant);
              }}
              isArabic={isArabic}
              COLORS={COLORS}
              isMobile={isMobile}
            />
          )}



          <div
            className="d-flex gap-3 mt-4"
            style={{
              flexDirection: isArabic ? "row-reverse" : "row",
              justifyContent: "space-between",
            }}
          >
            <button
              onClick={() => navigate("/company-info")}
              className="btn fw-semibold py-2"
              style={{
                flex: 1,
                background: "rgba(255,255,255,0.1)",
                color: COLORS.textLight,
                borderRadius: 10,
                border: `1.5px solid ${COLORS.gold}`,
                transition: "0.3s",
              }}
              onMouseOver={(e) => (
                (e.currentTarget.style.background = COLORS.gold),
                (e.currentTarget.style.color = COLORS.darkPurple)
              )}
              onMouseOut={(e) => (
                (e.currentTarget.style.background =
                  "rgba(255,255,255,0.1)"),
                (e.currentTarget.style.color = COLORS.textLight)
              )}
            >
              {isArabic ? "رجوع" : "Back"}
            </button>

            <button
              onClick={handleNext}
              className="btn fw-semibold py-2"
              style={{
                flex: 1,
                background: COLORS.gold,
                color: COLORS.darkPurple,
                borderRadius: 10,
                boxShadow: "0 4px 15px rgba(242,180,65,0.4)",
                transition: "0.3s",
                border: "none",
                opacity: nextLoading ? 0.7 : 1,
                fontSize: isMobile ? "0.95rem" : "1rem",
                padding: isMobile ? "10px" : "12px",
              }}
              disabled={nextLoading}
              onMouseOver={(e) =>
                !nextLoading && (e.currentTarget.style.opacity = "0.9")
              }
              onMouseOut={(e) =>
                !nextLoading && (e.currentTarget.style.opacity = "1")
              }
            >
              {nextLoading
                ? isArabic
                  ? "جارٍ التحميل..."
                  : "Loading..."
                : isArabic
                  ? "التالي"
                  : "Next"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
